/**
 * Demo Configuration
 * Defines all 16 steps of the Interactive Demo/Tutorial feature
 * Each step includes target element, positioning, and simulation action
 */

/**
 * Step configuration object structure:
 * @typedef {Object} DemoStep
 * @property {string} id - Unique identifier for the step
 * @property {number} stepNumber - Sequential step number (1-16)
 * @property {string} title - Step title displayed to user
 * @property {string} description - Detailed description of the action
 * @property {string} instruction - Action prompt for the user
 * @property {string} pageRoute - Vue Router path where this step occurs
 * @property {string} targetElement - CSS selector for the element to highlight
 * @property {string} position - Tooltip position relative to target (top, bottom, left, right)
 * @property {string} action - Expected user action (click, scroll, view, etc.)
 * @property {Array<string>} prerequisite - Array of step IDs that must be completed first
 * @property {boolean} waitForVisibility - If true, wait for target element to be visible before showing highlight
 * @property {{ type: 'click'|'navigate'|'polygon-click', selector?: string, route?: string }} simulateAction - Action to perform automatically when Next is clicked
 */

import { SAMPLE_SUBJECT, SAMPLE_VOCABULARY } from './demoConstants.js';

export const DEMO_STEPS = [
  {
    id: 'subject-list',
    stepNumber: 1,
    pageRoute: '/userprofile',
    title: 'Explore Subjects',
    description: 'Start by navigating to the Library to explore available subjects and resources.',
    instruction: 'Click on the Library button in the menu to see available subjects',
    targetElement: 'a[href="/subjectslist"], .sidebar-style a[href="/subjectslist"]',
    position: 'right',
    action: 'click',
    prerequisite: [],
    waitForVisibility: false,
    simulateAction: { type: 'navigate', route: '/subjectslist' },
  },
  {
    id: 'select-subject',
    stepNumber: 2,
    pageRoute: '/subjectslist',
    title: 'Select a Subject',
    description: 'Click on a subject to view and manage its contents. Start by exploring the Samples.',
    instruction: 'Click on a subject to open it',
    targetElement: '[data-test="subject-card-0"]',
    position: 'right',
    action: 'click',
    prerequisite: ['subject-list'],
    waitForVisibility: true,
    simulateAction: { type: 'click', selector: '[data-test="subject-card-0"]' },
  },
  {
    id: 'open-first-file',
    stepNumber: 3,
    pageRoute: '/knowledgebase',
    title: 'Open a Document',
    description: 'Here you can find your uploaded resources. Upload new ones using the button at the top-right, or search for web resources using the search bar above.',
    instruction: 'Click on the first document to open it.',
    targetElement: '[data-test="resource-item-0"]',
    position: 'right',
    action: 'click',
    prerequisite: ['select-subject'],
    waitForVisibility: true,
    simulateAction: { type: 'click', selector: '[data-test="resource-item-0"]' },
  },
  {
    id: 'polygon-vocabulary-lookup',
    stepNumber: 4,
    pageRoute: '/reader',
    title: 'Look Up Vocabulary',
    description: 'Scroll the document and click on any highlighted word or phrase to look up its definition.',
    instruction: 'Click the highlighted text in the document.',
    targetElement: '[data-test="viewer-section"]',
    position: 'top',
    action: 'click',
    prerequisite: ['open-first-file'],
    waitForVisibility: true,
    elementWaitTimeout: 5000,
    simulateAction: { type: 'highlight-text', selector: '[data-test="text-content"]', value: SAMPLE_VOCABULARY },
  },
  {
    id: 'save-vocabulary',
    stepNumber: 5,
    pageRoute: '/reader',
    title: 'Save to Vocabulary',
    description: 'Add this word to your personal vocabulary list for future reference.',
    instruction: 'Click the Save button.',
    targetElement: '[data-test="save-vocab-btn-0-0"]',
    position: 'top',
    action: 'click',
    prerequisite: ['polygon-vocabulary-lookup'],
    waitForVisibility: true,
    elementWaitTimeout: 5000,
    simulateAction: { type: 'click', selector: '[data-test="save-vocab-btn-0-0"]' },
  },
  {
    id: 'extract-vocabulary',
    stepNumber: 6,
    pageRoute: '/reader',
    title: 'Extract All Vocabulary',
    description: 'Choose a topic to extract relevant vocabulary, choose a difficulty level to focus on words that match your proficiency, and generate the vocabulary list. You can also save words to review later.',
    instruction: 'Click the Extract Vocabulary button.',
    targetElement: '[data-test="extract-vocab-btn"]',
    position: 'bottom',
    prerequisite: ['save-vocabulary'],
  },
  {
    id: 'navigate-to-summary',
    stepNumber: 7,
    pageRoute: '/reader',
    title: 'Navigate to Summary',
    description: 'Switch to the Summary tab to generate or view a condensed overview of the document.',
    instruction: 'Click the Summary tab.',
    targetElement: '[data-test="summary-tab"]',
    position: 'bottom',
    action: 'click',
    prerequisite: ['extract-vocabulary'],
    simulateAction: { type: 'click', selector: '[data-test="summary-tab"]' },
  },
    {
    id: 'generate-summary',
    stepNumber: 8,
    pageRoute: '/reader',
    title: 'Generate Summary',
    description: 'Get a condensed overview of the key points in the document.',
    instruction: 'Click the Generate Summary button.',
    targetElement: '[data-test="generate-summary-btn"]',
    position: 'bottom',
    action: 'click',
    prerequisite: ['navigate-to-summary'],
    simulateAction: { type: 'click', selector: '[data-test="generate-summary-btn"]' },
  },
  {
    id: 'navigate-to-questions',
    stepNumber: 9,
    pageRoute: '/reader',
    title: 'Navigate to Questions',
    description: 'Switch to the Questions tab to generate comprehension questions at your chosen difficulty level.',
    instruction: 'Click the Questions tab.',
    targetElement: '[data-test="questions-tab"]',
    position: 'top',
    action: 'click',
    prerequisite: ['generate-summary'],
    simulateAction: { type: 'click', selector: '[data-test="questions-tab"]' },
  },
  {
    id: 'generate-questions',
    stepNumber: 10,
    pageRoute: '/reader',
    title: 'Generate Questions',
    description: 'Select a difficulty level and generate pre-reading or post-reading comprehension questions for the document.',
    instruction: 'Select a difficulty, then click Generate Questions.',
    targetElement: '[data-test="generate-questions-btn"]',
    position: 'top',
    action: 'click',
    prerequisite: ['navigate-to-questions'],
    simulateAction: { type: 'click', selector: '[data-test="generate-questions-btn"]' },
  },
  {
    id: 'listen',
    stepNumber: 11,
    pageRoute: '/reader',
    title: 'Listen to your Document',
    description: 'Listen to the document being read aloud.',
    instruction: 'Click the Listen button.',
    targetElement: '[data-test="listen-btn"]',
    position: 'top',
    action: 'click',
  },
  {
    id: 'view-vocabulary-list',
    stepNumber: 12,
    pageRoute: '/reader',
    title: 'View Your Vocabulary',
    description: 'Navigate to your vocabulary list to see all words you\'ve saved.',
    instruction: 'Click on the Review button in the sidebar to view your vocabulary',
    targetElement: 'a[href="/vocabularylist"], .sidebar-style a[href="/vocabularylist"]',
    position: 'right',
    action: 'click',
    simulateAction: { type: 'navigate', route: '/vocabularylist' },
  },
  {
    id: 'highlight-new-vocabulary',
    stepNumber: 13,
    pageRoute: '/vocabularylist',
    title: 'Your New Words',
    description: 'Review the vocabulary items you just created during this lesson.',
    instruction: 'Take a moment to review your newly added vocabulary words',
    targetElement: '[data-test="vocabulary-item-0"]',
    position: 'right',
    action: 'click',
    prerequisite: ['view-vocabulary-list'],
    waitForVisibility: true,
    simulateAction: { type: 'click', selector: '[data-test="vocabulary-item-0"]' },
  },
  {
    id: 'review-vocabulary',
    stepNumber: 14,
    pageRoute: '/vocabularylist',
    title: 'Practice Vocabulary',
    description: 'Start a review session to test and reinforce the words you have saved.',
    instruction: 'Click the Review button.',
    targetElement: '[data-test="review-btn"]',
    position: 'bottom',
    action: 'click',
    prerequisite: ['highlight-new-vocabulary'],
    simulateAction: { type: 'click', selector: '[data-test="review-btn"]' },
  },
  {
    id: 'review-vocabulary-flashcards',
    stepNumber: 15,
    pageRoute: '/vocabularyreview',
    title: 'Vocabulary Flashcards',
    description: 'Use flashcards to reinforce your memory and test how well you know the words from this session.',
    instruction: 'Flip through the flashcards and test your knowledge.',
    targetElement: '[data-test="flashcard-component"]',
    position: 'top',
    action: 'click',
    prerequisite: ['review-vocabulary'],
    waitForVisibility: true,
    simulateAction: { type: 'click', selector: '[data-test="flashcard-component"]' },
  },
  {
    id: 'view-user-profile',
    stepNumber: 16,
    pageRoute: '/vocabularyreview',
    title: 'View Your Progress',
    description: 'Check your user profile.',
    instruction: 'Click on your profile avatar to view your learning statistics',
    targetElement: '[data-test="profile-btn"]',
    position: 'bottom',
    action: 'click',
    prerequisite: ['review-vocabulary-flashcards'],
    simulateAction: { type: 'click', selector: '[data-test="profile-btn"]' },
  },
  {
    id: 'view-user-profile',
    stepNumber: 17,
    pageRoute: '/userprofile',
    title: 'View Your Progress',
    description: 'Here you can find learning statistics to track your growth.',
    instruction: 'Take a moment to review your learning statistics',
    targetElement: '[data-test="statistics-section"]',
    position: 'top',
  },
  {
    id: 'create-subject',
    stepNumber: 18,
    pageRoute: '/subjectslist',
    title: 'Create Your Own Content',
    description: 'Ready to study your own favourite subject? Create a new subject and upload your documents to get started.',
    instruction: 'Go to the subject menu and create a new subject.',
    targetElement: 'a[href="/subjectslist"], .sidebar-style a[href="/subjectslist"]',
    position: 'right',
    action: 'click',
    prerequisite: ['view-user-profile'],
    simulateAction: { type: 'navigate', route: '/subjectslist' },
  },
  {
    id: 'create-new-subject',
    stepNumber: 19,
    pageRoute: '/subjectslist',
    title: 'Create Your Own Content',
    description: 'Ready to study your own favourite subject? Create a new subject and upload your documents to get started.',
    instruction: 'Click "Create Subject" to add your own subject.',
    targetElement: '[data-test="create-subject-btn"]',
    position: 'left',
    action: 'click',
    prerequisite: ['view-user-profile'],
    simulateActions: [
      { type: 'click', selector: '[data-test="create-subject-btn"]' },
      { type: 'type-text', selector: '[data-test="subject-input"]', value: SAMPLE_SUBJECT },
      { type: 'click', selector: '[data-test="create-subject-save-btn"]' },
    ],
  },
  {
    id: 'view-new-subject',
    stepNumber: 20,
    pageRoute: '/subjectslist',
    title: 'Your New Subject',
    description: 'Your new subject has been created. You can now upload documents and start studying.',
    instruction: 'Your subject is ready — You can use it to upload new resources or to start a chatting session.',
    targetElement: '[data-test="subject-card-1"]',
    position: 'right',
    action: 'click',
    prerequisite: ['create-new-subject'],
    waitForVisibility: true,
    simulateActions: [{ type: 'click', selector: '[data-test="subject-card-1"]' }],
  },
  {
    id: 'navigate-to-chat',
    stepNumber: 22,
    pageRoute: '/subjectslist',
    title: 'Navigate to Chat',
    description: 'Switch to the Chat tab to start a new chat session.',
    instruction: 'Click the Chat tab.',
    targetElement: 'a[href="/chat"], .sidebar-style a[href="/chat"]',
    position: 'bottom',
    action: 'click',
    simulateAction: { type: 'click', selector: '[data-test="chat-tab"]' },
  },
  {
    id: 'chat-experience',
    stepNumber: 23,
    pageRoute: '/chat',
    title: 'Create a new chat session',
    description: 'Start here to use the chat feature.',
    instruction: 'Click the "New Conversation" button to begin.',
    targetElement: '[data-test="new-conversation-btn"]',
    position: 'bottom',
    action: 'click',
    simulateAction: { type: 'click', selector: '[data-test="new-conversation-btn"]' },
  },
  {
    id: 'select-scenario-subject',
    stepNumber: 24,
    pageRoute: '/chat/new',
    title: 'Select a Scenario Subject',
    description: 'Select a subject to generate scenarios for your chat session.',
    instruction: 'Click on "Start Chat" to begin your chat session.',
    targetElement: '[data-test="subject-select"]',
    position: 'bottom',
    action: 'click',
    simulateAction: { type: 'select', selector: '[data-test="subject-select"]', choice: SAMPLE_SUBJECT },
  },
  {
    id: 'new-chat-scenarios',
    stepNumber: 25,
    pageRoute: '/chat/new',
    title: 'Scenario-based chat',
    description: 'Generate scenarios first using your favourite topic',
    instruction: 'Click the "Generate" button.',
    targetElement: '[data-test="generate-scenarios-btn"]',
    position: 'bottom',
    action: 'click',
    simulateAction: { type: 'click', selector: '[data-test="generate-scenarios-btn"]' },
  },
  {
    id: 'start-chat',
    stepNumber: 26,
    pageRoute: '/chat/new',
    title: 'Choose a Scenario and Start Chatting',
    description: 'Choose a scenario to start a chat session based on your selected topic.',
    instruction: 'Select a scenario from the list to begin chatting.',
    targetElement: '[data-test="start-chat-btn"]',
    position: 'right',
    action: 'click',
    prerequisite: ['new-chat-scenarios'],
    simulateAction: { type: 'click', selector: '[data-test="start-chat-btn"]' },
  },
  {
    id: 'chat-here',
    stepNumber: 27,
    pageRoute: '/chat/session',
    title: 'Chat Here',
    description: 'Chat to practice what you have learned.',
    instruction: 'Type your message in the chat box to start chatting.',
    targetElement: '[data-test="chat-session-container"]',
    position: 'top',
    action: 'click',
  }
];

export default DEMO_STEPS;
